import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { parse, stringify } from '../../util/urlHelpers';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Button, LandingPageIntro, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import MarketplaceBrowseControls from './MarketplaceBrowseControls';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';
import { filterDebugSummary, organizeMarketplaceFilters } from './marketplaceFilterOrganization';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = 0;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      isDesktopFiltersOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      draftInitialValues: {},
      draftRevision: 0,
    };
    this.mobileFilters = React.createRef();
    this.desktopDrawer = React.createRef();

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.onToggleDesktopFilters = this.onToggleDesktopFilters.bind(this);
    this.onToggleFilters = this.onToggleFilters.bind(this);
    this.onCloseDesktopFilters = this.onCloseDesktopFilters.bind(this);
    this.onDesktopFilterKeyDown = this.onDesktopFilterKeyDown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.hasLoggedFilterDiagnostics = false;

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleClearFilter = this.handleClearFilter.bind(this);
    this.applyDraft = this.applyDraft.bind(this);
    this.clearDraft = this.clearDraft.bind(this);
    this.changeDraft = this.changeDraft.bind(this);
    this.draftQueryParams = {};
    this.draftChangedKeys = new Set();
  }

  initializeDraft() {
    const applied = validUrlQueryParamsFromProps(this.props);
    this.draftQueryParams = { ...applied };
    this.draftChangedKeys = new Set();
    this.setState(state => ({
      draftInitialValues: applied,
      draftRevision: state.draftRevision + 1,
    }));
  }

  changeDraft(values) {
    this.draftQueryParams = { ...this.draftQueryParams, ...values };
    Object.keys(values).forEach(key => this.draftChangedKeys.add(key));
    this.setState({ currentQueryParams: this.draftQueryParams });
  }

  clearDraft() {
    const applied = validUrlQueryParamsFromProps(this.props);
    const { queryParamName } = this.props.config.search.sortConfig;
    this.draftChangedKeys.delete('keywords');
    this.draftChangedKeys.delete(queryParamName);
    this.draftQueryParams = Object.fromEntries(
      Object.keys({ ...applied, ...this.draftQueryParams }).map(key => [
        key,
        key === 'keywords' || key === queryParamName ? applied[key] : null,
      ])
    );
    this.setState(state => ({
      draftInitialValues: { ...this.draftQueryParams },
      draftRevision: state.draftRevision + 1,
    }));
  }

  applyDraft() {
    const draftFilters = { ...this.draftQueryParams };
    const { queryParamName } = this.props.config.search.sortConfig;
    if (!this.draftChangedKeys.has('keywords')) delete draftFilters.keywords;
    if (!this.draftChangedKeys.has(queryParamName)) delete draftFilters[queryParamName];
    this.getHandleChangedValueFn(true)(draftFilters);
    this.onCloseDesktopFilters();
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.initializeDraft();
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.draftQueryParams = {};
    this.draftChangedKeys = new Set();
    this.setState({ isMobileModalOpen: false });
  }

  onToggleDesktopFilters() {
    if (this.state.isDesktopFiltersOpen) return this.onCloseDesktopFilters();
    this.initializeDraft();
    this.setState({ isDesktopFiltersOpen: true });
  }

  onToggleFilters(event) {
    this.filterOpener = event?.currentTarget;
    if (window.innerWidth < MODAL_BREAKPOINT) {
      this.mobileFilters.current?.openFilters();
    } else {
      this.onToggleDesktopFilters();
    }
  }

  onCloseDesktopFilters() {
    this.draftQueryParams = {};
    this.draftChangedKeys = new Set();
    this.setState({ isDesktopFiltersOpen: false });
  }

  onDesktopFilterKeyDown(event) {
    if (!this.state.isDesktopFiltersOpen) return;
    if (event.key === 'Escape') this.onCloseDesktopFilters();
    if (event.key === 'Tab') {
      const focusable = Array.from(
        this.desktopDrawer.current.querySelectorAll('button, input, select, [tabindex="0"]')
      ).filter(
        element =>
          !element.disabled && !element.closest('[inert]') && element.getClientRects().length
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onDesktopFilterKeyDown);
    window.addEventListener('resize', this.onViewportChange);
  }

  onViewportChange() {
    if (window.innerWidth < MODAL_BREAKPOINT && this.state.isDesktopFiltersOpen) {
      this.onCloseDesktopFilters();
    } else if (window.innerWidth >= MODAL_BREAKPOINT && this.state.isMobileModalOpen) {
      this.mobileFilters.current?.closeFilters();
    }
  }

  componentDidUpdate(_previousProps, previousState) {
    if (previousState.isDesktopFiltersOpen !== this.state.isDesktopFiltersOpen) {
      this.props.onManageDisableScrolling(
        'SearchPageWithGrid.desktopFilters',
        this.state.isDesktopFiltersOpen
      );
      if (this.state.isDesktopFiltersOpen) {
        this.desktopDrawer.current?.querySelector('button')?.focus();
      } else {
        this.filterOpener?.focus();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onDesktopFilterKeyDown);
    window.removeEventListener('resize', this.onViewportChange);
    this.props.onManageDisableScrolling('SearchPageWithGrid.desktopFilters', false);
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  handleClearFilter(key) {
    this.getHandleChangedValueFn(true)({ [key]: null });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    const applied = validUrlQueryParamsFromProps(this.props);
    const { queryParamName } = this.props.config.search.sortConfig;
    this.getHandleChangedValueFn(true)(
      Object.fromEntries(
        Object.keys(applied)
          .filter(key => key !== 'keywords' && key !== queryParamName)
          .map(key => [key, null])
      )
    );

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );
    const marketplaceIntro = !listingTypePathParam ? (
      <LandingPageIntro
        title="Marketplace"
        description="Browse books, special editions, and bookish finds from Canadian sellers."
        compactTop
      />
    ) : null;
    const marketplaceBrowseControls = !listingTypePathParam ? (
      <MarketplaceBrowseControls
        config={config}
        history={this.props.history}
        routeConfiguration={routeConfiguration}
        validQueryParams={validQueryParams}
        onToggleFilters={this.onToggleFilters}
        selectedFiltersCount={selectedFiltersCountForMobile}
      />
    ) : null;
    const draftAvailableFilters =
      this.state.isDesktopFiltersOpen || this.state.isMobileModalOpen
        ? getDerivedRenderData({
            intl,
            config,
            routeConfiguration,
            currentPathParams,
            location: { ...location, search: `?${stringify(this.draftQueryParams)}` },
          }).availableFilters
        : availableFilters;
    const organizedFilters = organizeMarketplaceFilters(draftAvailableFilters);
    const activeFilterChips = Object.entries(selectedFilters || {}).filter(
      ([key, value]) =>
        key !== 'keywords' &&
        key !== sortConfig.queryParamName &&
        key !== 'pub_listingType' &&
        value != null &&
        value !== ''
    );
    const activeFilterLabel = key => {
      const paramKey = key.replace(/^pub_/, '');
      const configuredFilter = organizedFilters.find(filter => filter.key === paramKey);
      return configuredFilter?.filterConfig?.label || paramKey.replace(/([A-Z])/g, ' $1');
    };
    const isISOPage = listingTypePathParam === 'iso-book';
    const filtersMaybe = !isISOPage ? organizedFilters : [];
    const isoIntro = isISOPage ? (
      <LandingPageIntro
        title="In Search Of"
        description="See what PageHop readers are looking for. Have one on your shelf? You may have found your buyer."
        compactTop
      />
    ) : null;

    if (process.env.NODE_ENV === 'development' && !this.hasLoggedFilterDiagnostics) {
      console.info('Marketplace filter drawer keys', {
        rawAvailableFilterKeys: availableFilters.map(filter => filter.key),
        organizedFilterKeys: organizedFilters.map(filter => filter.key),
      });
      console.table({
        availableFilters: filterDebugSummary(availableFilters),
        organizedFilters: filterDebugSummary(organizedFilters),
      });
      this.hasLoggedFilterDiagnostics = true;
    }

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses =
      this.state.isMobileModalOpen || this.state.isDesktopFiltersOpen
        ? classNames(css.topbarBehindModal, css.topbar)
        : css.topbar;
    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          {this.state.isDesktopFiltersOpen ? (
            <button
              type="button"
              className={css.filterDrawerBackdrop}
              aria-label="Dismiss filters"
              onClick={this.onCloseDesktopFilters}
            />
          ) : null}
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div
              ref={this.desktopDrawer}
              role={this.state.isDesktopFiltersOpen ? 'dialog' : undefined}
              aria-modal={this.state.isDesktopFiltersOpen ? true : undefined}
              aria-label={intl.formatMessage({ id: 'SearchFiltersMobile.heading' })}
              className={classNames(css.filterColumnContent, {
                [css.filterColumnContentOpen]: this.state.isDesktopFiltersOpen,
              })}
            >
              <div className={css.filterDrawerHeader}>
                <strong>
                  <FormattedMessage id="SearchFiltersMobile.heading" />
                </strong>
                <button
                  type="button"
                  onClick={this.onCloseDesktopFilters}
                  aria-label="Close filters"
                >
                  ×
                </button>
              </div>
              <div className={css.filterDrawerBody}>
                {this.state.isDesktopFiltersOpen
                  ? filtersMaybe.map(filterConfig => {
                      const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                        filterConfig.key
                      }`;
                      const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                      return (
                        <FilterComponent
                          key={`${key}.${this.state.draftRevision}`}
                          id={filterId}
                          className={css.filter}
                          data-filter-key={filterConfig.key}
                          config={filterConfig}
                          containerId="SearchPageWithGrid_DesktopFilters"
                          listingCategories={listingCategories}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={names =>
                            Object.fromEntries(
                              names.map(name => [name, this.state.draftInitialValues[name]])
                            )
                          }
                          getHandleChangedValueFn={() => this.changeDraft}
                          intl={intl}
                          liveEdit
                          draftMode
                          showAsPopup={false}
                          isDesktop
                          initiallyOpen={false}
                        />
                      );
                    })
                  : null}
              </div>
              {this.state.isDesktopFiltersOpen ? (
                <div className={css.filterDrawerActions}>
                  <p className={css.draftHint}>
                    <FormattedMessage id="SearchPage.draftHint" />
                  </p>
                  <button type="button" className={css.resetAllButton} onClick={this.clearDraft}>
                    <FormattedMessage id="SearchPage.clearDraft" />
                  </button>
                  <Button onClick={this.applyDraft}>
                    <FormattedMessage id="SearchPage.applyFilters" />
                  </Button>
                </div>
              ) : null}
            </div>
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              {marketplaceIntro}
              {isoIntro}
              {marketplaceBrowseControls}
              {activeFilterChips.length ? (
                <div className={css.activeFilters} aria-label="Active filters">
                  {activeFilterChips.map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => this.handleClearFilter(key)}
                    >{`${activeFilterLabel(key)}: ${value} ×`}</button>
                  ))}
                  <button type="button" onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id="SearchPage.clearApplied" />
                  </button>
                </div>
              ) : null}
              {!isISOPage ? (
                <SearchFiltersMobile
                  ref={this.mobileFilters}
                  className={css.searchFiltersMobileList}
                  urlQueryParams={validQueryParams}
                  sortByComponent={sortBy('mobile')}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  showAsModalMaxWidth={MODAL_BREAKPOINT - 1}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onOpenModal={this.onOpenMobileModal}
                  onCloseModal={this.onCloseMobileModal}
                  resetAll={this.clearDraft}
                  onApply={this.applyDraft}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                  isMapVariant={false}
                  showOpenButton={false}
                  noResultsInfo={noResultsInfo}
                  location={location}
                >
                  {filtersMaybe.map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                    return (
                      <FilterComponent
                        key={`${key}.${this.state.draftRevision}`}
                        id={filterId}
                        config={filterConfig}
                        containerId="SearchPage_MobileFilters"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={names =>
                          Object.fromEntries(
                            names.map(name => [name, this.state.draftInitialValues[name]])
                          )
                        }
                        getHandleChangedValueFn={() => this.changeDraft}
                        intl={intl}
                        liveEdit
                        draftMode
                        showAsPopup={false}
                        initiallyOpen={false}
                      />
                    );
                  })}
                </SearchFiltersMobile>
              ) : null}
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                selectedFiltersCount={selectedFiltersCountForMobile}
                onToggleFilters={this.onToggleDesktopFilters}
                showFiltersButton={false}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listingsAreLoaded && !searchListingsError ? listings : []}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  paginationPageName={currentPathParams.pageRouteName}
                  intl={intl}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );
};

export default SearchPage;
